import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BellRing,
  BookOpenCheck,
  BrainCircuit,
  Briefcase,
  Building2,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  CreditCard,
  FileClock,
  FileSignature,
  KeyRound,
  LifeBuoy,
  ListChecks,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Tags,
  UserCog,
  UsersRound
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

const GUIDE_SECTIONS = [
  {
    key: 'dashboard',
    name: 'Dashboard',
    icon: BarChart3,
    summary: 'Start here when you want to know what is happening across the platform right now.',
    pages: [
      {
        title: 'Dashboard',
        path: '/dashboard',
        icon: BarChart3,
        simple: 'This is the main control screen. It gives you a quick but useful picture of the whole platform.',
        useFor: [
          'Check the most important platform numbers without opening every page one by one.',
          'See account, KYC, job, interview, offer, billing, queue, AI usage, and recent activity signals.',
          'Spot areas that may need action before users complain.'
        ],
        steps: [
          'Open Dashboard from the top of the sidebar.',
          'Read the top cards first. They show the biggest platform signals.',
          'Check the charts to understand status spread, not just total counts.',
          'Review the Needs Attention area when it appears. It points you to items that may need follow-up.',
          'Use recent activity to understand what has changed lately.'
        ],
        careful: [
          'Dashboard numbers are summaries. Open the matching detailed page before making a major decision.',
          'Revenue is shown by currency where needed. Do not add different currencies together unless the system has converted them.'
        ]
      }
    ]
  },
  {
    key: 'employers',
    name: 'Employers',
    icon: Building2,
    summary: 'Use these pages to manage company accounts and their verification journey.',
    pages: [
      {
        title: 'Employer list',
        path: '/employers',
        icon: Building2,
        simple: 'This page lists companies using Sanspath.',
        useFor: [
          'Find employers by company, email, account state, region, or activity.',
          'Open an employer record to review the company profile, jobs, KYC state, and recent platform activity.',
          'Suspend or reactivate an employer account when policy allows it.'
        ],
        steps: [
          'Open Employers, then Employer list.',
          'Use search or filters to find the company.',
          'Check the table columns before taking action. Look at status, KYC, jobs, activity, and contact details.',
          'Click View when you need the full record.',
          'Use Suspend only when the account should be blocked from normal use. Use Reactivate when the issue has been cleared.'
        ],
        careful: [
          'Never expose or ask for passwords. Passwords are not shown here.',
          'Suspending an employer affects their ability to operate on the platform. Confirm the reason first.'
        ]
      },
      {
        title: 'Employer KYC',
        path: '/employers/kyc',
        icon: ClipboardCheck,
        simple: 'This page is for checking if a company is real and allowed to use the platform.',
        useFor: [
          'Review submitted company documents and director documents.',
          'Approve, reject, or request changes on employer KYC submissions.',
          'See regional document requirements and reviewer notes.',
          'Track which employers are pending, approved, rejected, or need more information.'
        ],
        steps: [
          'Open Employers, then Employer KYC.',
          'Pick a submission from the queue.',
          'Check the company details and each required document.',
          'Open document previews where available and compare them with the submitted company information.',
          'Add a clear reviewer note before approving, rejecting, or requesting changes.',
          'Save the decision and confirm that the KYC status changed as expected.'
        ],
        careful: [
          'KYC decisions affect trust and safety. Do not approve unclear, missing, expired, or mismatched documents.',
          'Use simple reviewer notes. They should explain the decision without insulting the employer.'
        ]
      }
    ]
  },
  {
    key: 'candidates',
    name: 'Candidates',
    icon: UsersRound,
    summary: 'Use this section to understand candidate accounts, readiness, and platform activity.',
    pages: [
      {
        title: 'Candidate list',
        path: '/candidates',
        icon: UsersRound,
        simple: 'This page lists people looking for opportunities on Sanspath.',
        useFor: [
          'Find candidates by name, email, phone, region, visibility state, readiness, and activity.',
          'Review profile information such as CV state, skills, education, experience, preferences, and match activity.',
          'Suspend or reactivate a candidate account when required.'
        ],
        steps: [
          'Open Candidates, then Candidate list.',
          'Search for the candidate or use filters to narrow the table.',
          'Open the candidate record when you need more context.',
          'Check profile readiness, CV status, visibility, matches, interviews, assessments, and offers before taking action.',
          'Use account actions only after confirming the user and the reason.'
        ],
        careful: [
          'Candidate data is sensitive personal data. Only open records for a valid admin reason.',
          'Profile readiness is a guide. It should not be treated as a final judgment about a person.'
        ]
      }
    ]
  },
  {
    key: 'jobs',
    name: 'Jobs',
    icon: Briefcase,
    summary: 'Use these pages to watch job quality, job state, and matching performance.',
    pages: [
      {
        title: 'Job list',
        path: '/jobs',
        icon: Briefcase,
        simple: 'This page lists jobs posted by employers.',
        useFor: [
          'Find jobs by employer, title, status, location, salary, currency, and activity.',
          'Open a job to inspect its details, employer, matching state, and operational history.',
          'Watch for jobs that are incomplete, paused, inactive, or causing matching issues.'
        ],
        steps: [
          'Open Jobs, then Job list.',
          'Use search or filters to find the job.',
          'Check employer, job title, status, location, salary, currency, and created date.',
          'Open the job when you need more detail.',
          'Use status actions only when you understand the employer impact.'
        ],
        careful: [
          'A job can affect candidate matching. Do not change status casually.',
          'If salary currency looks wrong, review the job details and conversion notes before acting.'
        ]
      },
      {
        title: 'Job insights',
        path: '/jobs/insights',
        icon: BrainCircuit,
        simple: 'This page explains how matching is performing for jobs.',
        useFor: [
          'See matching funnel counts for a job.',
          'Check matching timing, AI usage, token use, and estimated provider cost where available.',
          'Find jobs where matching may be slow, empty, or expensive.'
        ],
        steps: [
          'Open Jobs, then Job insights.',
          'Search for or select the job you want to inspect.',
          'Review funnel numbers from eligible candidates to deeper match stages.',
          'Check timing and AI usage so you can spot cost or performance problems.',
          'Use the evidence to decide if the job data, matching settings, or candidate pool needs review.'
        ],
        careful: [
          'This page explains behavior. It is not a replacement for reviewing the actual job and candidate records.',
          'AI usage can cost money. Investigate unusual spikes before increasing limits.'
        ]
      }
    ]
  },
  {
    key: 'hiring',
    name: 'Hiring flow',
    icon: CalendarClock,
    summary: 'Use this section to follow what happens after matching: interviews, assessments, and offers.',
    pages: [
      {
        title: 'Interviews',
        path: '/interviews',
        icon: CalendarClock,
        simple: 'This page shows interview activity between employers and candidates.',
        useFor: [
          'Review interviews by status, date, employer, candidate, and job.',
          'See whether interviews are requested, scheduled, completed, cancelled, or need action.',
          'Support users when there is a scheduling or status problem.'
        ],
        steps: [
          'Open Hiring flow, then Interviews.',
          'Use filters to find the interview record.',
          'Check the candidate, employer, job, time, interview type, and current status.',
          'Open the record to understand the history before helping either side.',
          'Use logs if you need to confirm who changed the interview status.'
        ],
        careful: [
          'Do not change interview information without a clear support reason.',
          'Time zones matter. Check displayed date and time carefully.'
        ]
      },
      {
        title: 'Assessments',
        path: '/assessments',
        icon: ListChecks,
        simple: 'This page shows assessment activity connected to hiring.',
        useFor: [
          'Review assessments created by employers and assessment attempts by candidates.',
          'Check status, completion, score, timing, and job connection.',
          'Investigate assessment problems such as missing results or stuck attempts.'
        ],
        steps: [
          'Open Hiring flow, then Assessments.',
          'Find the assessment by candidate, employer, job, status, or date.',
          'Open the record to inspect progress and scoring details.',
          'If a result looks wrong, compare it with the assessment logs.',
          'Escalate scoring or integrity issues instead of editing results manually.'
        ],
        careful: [
          'Assessment results can affect hiring decisions. Treat them carefully.',
          'If monitoring detected tab switching or timing issues, review context before making a decision.'
        ]
      },
      {
        title: 'Offers',
        path: '/offers',
        icon: FileSignature,
        simple: 'This page shows offer activity after an employer wants to hire a candidate.',
        useFor: [
          'Review offers, negotiation state, signing state, uploads, and attached documents.',
          'Check whether offers are draft, sent, accepted, declined, negotiated, withdrawn, or completed.',
          'Support users when offer letters or contract uploads fail.'
        ],
        steps: [
          'Open Hiring flow, then Offers.',
          'Find the offer by candidate, employer, job, status, or date.',
          'Open the record to review terms, documents, signatures, and timeline.',
          'Check whether the candidate and employer actions are complete.',
          'Use logs for disputes about who changed or uploaded something.'
        ],
        careful: [
          'Offer letters and contracts are sensitive documents.',
          'Do not change offer records unless you have a clear authorization trail.'
        ]
      }
    ]
  },
  {
    key: 'communications',
    name: 'Communications',
    icon: BellRing,
    summary: 'Use this section to send platform messages and manage the WhatsApp sender.',
    pages: [
      {
        title: 'Campaigns',
        path: '/communications/notifications',
        icon: BellRing,
        simple: 'This page lets admins send controlled messages to users.',
        useFor: [
          'Send candidate in-app messages, candidate WhatsApp messages, or employer emails where supported.',
          'Target recipients by audience, specific users, region, country, or other available filters.',
          'Track delivery state, queued work, and failed sends.'
        ],
        steps: [
          'Open Communications, then Campaigns.',
          'Choose the audience and channel.',
          'Write a short, clear message. Use placeholders only when you know what they will show.',
          'Review recipient count and targeting before sending.',
          'Acknowledge bulk sending when the system asks you to confirm.',
          'After sending, check delivery status and failed items.'
        ],
        careful: [
          'Bulk messages can reach many people. Review spelling, audience, channel, and timing before sending.',
          'WhatsApp messages should respect opt-outs. Do not send promotional messages to opted-out users.'
        ]
      },
      {
        title: 'WhatsApp sender',
        path: '/communications/whatsapp',
        icon: Smartphone,
        simple: 'This page connects the one WhatsApp number used by Sanspath to send WhatsApp messages.',
        useFor: [
          'See if a WhatsApp sender is connected.',
          'Connect a sender by scanning the QR code.',
          'Disconnect a sender when the wrong phone is connected or the device must be replaced.',
          'Confirm the connected device profile used for the sender.'
        ],
        steps: [
          'Open Communications, then WhatsApp sender.',
          'Check the connection status card first.',
          'If no sender is connected, start QR pairing and scan the code from the WhatsApp phone.',
          'Wait for the page to show connected status.',
          'If the wrong number is connected, disconnect it and pair the correct phone.'
        ],
        careful: [
          'Only one WhatsApp sender should be connected at a time.',
          'Disconnecting the sender can stop WhatsApp OTPs and notifications until it is connected again.'
        ]
      }
    ]
  },
  {
    key: 'support',
    name: 'Support',
    icon: LifeBuoy,
    summary: 'Use this section to handle customer support tickets from candidates, employers, and admins.',
    pages: [
      {
        title: 'Tickets',
        path: '/support/tickets',
        icon: LifeBuoy,
        simple: 'This page is the support workspace for triaging issues, assigning ownership, setting ETA, and resolving tickets.',
        useFor: [
          'Review tickets created by candidates, employers, and admins.',
          'Assign tickets to support admins or super admins.',
          'Update status, priority, category, ETA, and resolution notes.',
          'Add timeline updates and open attached screenshots or PDFs.'
        ],
        steps: [
          'Open Support, then Tickets.',
          'Use filters to find the right ticket by requester, priority, status, or assignee.',
          'Open the ticket and review the description, requester, attachment, and timeline.',
          'Assign an owner, set an ETA, and add a clear update.',
          'When the issue is complete, set the status to Resolved and include a resolution summary.'
        ],
        careful: [
          'Only open user records or attachments for a valid support reason.',
          'Use internal notes for team-only context and public updates for anything the requester can safely see.'
        ]
      }
    ]
  },
  {
    key: 'configuration',
    name: 'Configuration',
    icon: SlidersHorizontal,
    summary: 'Use configuration pages to control how the platform behaves. Changes here can affect many users.',
    pages: [
      {
        title: 'Platform configs',
        path: '/configurations',
        icon: SlidersHorizontal,
        simple: 'This page manages important platform settings.',
        useFor: [
          'Review and change matching settings, platform rules, AI settings, limits, and other configurable behavior.',
          'Understand what each setting does before changing it.',
          'Control changes in a clear and auditable way.'
        ],
        steps: [
          'Open Configuration, then Platform configs.',
          'Find the setting you want to review.',
          'Read the information icon or helper text before changing it.',
          'Change one setting at a time when possible.',
          'Save, then check the affected area of the system.'
        ],
        careful: [
          'A small setting can change matching, costs, or user access.',
          'Do not change production settings just to test an idea. Use a safe test path first.'
        ]
      },
      {
        title: 'Tag taxonomy',
        path: '/configurations/taxonomy',
        icon: Tags,
        simple: 'This page keeps skill and job tags clean.',
        useFor: [
          'Manage canonical tags, aliases, and suggested tags.',
          'Merge or map terms that mean the same thing, such as React JS and React.js.',
          'Stop duplicate or messy tags from weakening matching.'
        ],
        steps: [
          'Open Configuration, then Tag taxonomy.',
          'Search for the tag or alias you want to check.',
          'Open the canonical tag to inspect aliases and category.',
          'Add aliases when different words mean the same skill or concept.',
          'Review suggested tags and approve, merge, reject, or ignore them.'
        ],
        careful: [
          'Taxonomy affects matching quality. Do not merge tags unless they truly mean the same thing.',
          'Keep categories correct. The same word can mean different things in different contexts.'
        ]
      },
      {
        title: 'Question banks',
        path: '/configurations/question-banks',
        icon: BookOpenCheck,
        simple: 'This page manages questions asked to candidates, employers, or jobs.',
        useFor: [
          'Create and update governed question sets.',
          'Review question versions, answer types, and revision history.',
          'Keep profile and matching questions clear and consistent.'
        ],
        steps: [
          'Open Configuration, then Question banks.',
          'Choose the question group you want to manage.',
          'Review the current question text, answer type, options, and status.',
          'Make changes carefully and save a clear revision.',
          'Check the user flow that uses the question.'
        ],
        careful: [
          'Changing a question can change how future answers are collected.',
          'Do not delete or change question meaning without checking historical answer impact.'
        ]
      },
      {
        title: 'Admins & RBAC',
        path: '/configurations/admins',
        icon: UserCog,
        simple: 'This page controls who can use the Admin portal and what they are allowed to do.',
        useFor: [
          'Invite admin users.',
          'Assign roles and permissions.',
          'Restrict access to sensitive sections such as billing, KYC, configuration, logs, and monitoring.',
          'Deactivate admin access when someone should no longer enter the portal.'
        ],
        steps: [
          'Open Configuration, then Admins & RBAC.',
          'Review existing admins and their roles.',
          'Invite a new admin only with the correct email address.',
          'Choose the smallest role or permission set that lets them do their job.',
          'After saving, confirm the admin appears with the expected role.'
        ],
        careful: [
          'Admin access is powerful. Give only the access a person needs.',
          'Never share admin accounts. Every admin should use their own account.'
        ]
      }
    ]
  },
  {
    key: 'finance',
    name: 'Finance',
    icon: CreditCard,
    summary: 'Use this section to review billing, subscriptions, transactions, prices, and currency settings.',
    pages: [
      {
        title: 'Billing',
        path: '/finance/billing',
        icon: CreditCard,
        simple: 'This page manages payments and subscription settings.',
        useFor: [
          'Review subscriptions, transactions, billing offers, access expiry, payment states, and revenue by currency.',
          'Change subscription amount or currency when the business approves it.',
          'Set an effective date so a price change starts at the right time.',
          'Understand Paystack payment status and billing history.'
        ],
        steps: [
          'Open Finance, then Billing.',
          'Check the overview cards before editing anything.',
          'Open subscriptions or transactions to inspect details.',
          'When changing an amount or currency, enter the new value and effective date.',
          'Save the change and confirm it appears in the billing schedule.',
          'Review logs if there is a dispute about billing changes.'
        ],
        careful: [
          'Billing changes affect money. Confirm approval before changing prices.',
          'Currency must be handled carefully. Do not treat GBP, USD, KES, or NGN as the same value.'
        ]
      }
    ]
  },
  {
    key: 'logs',
    name: 'Logs',
    icon: FileClock,
    summary: 'Logs show important activity trails. They help answer who did what, when, and where.',
    pages: [
      {
        title: 'Log overview',
        path: '/logs',
        icon: FileClock,
        simple: 'This page gives a broad view of platform audit events.',
        useFor: [
          'See recent important admin and user activity across the system.',
          'Filter audit events by actor, action, domain, status, or date.',
          'Start an investigation before opening a specific log page.'
        ],
        steps: [
          'Open Logs, then Log overview.',
          'Use filters to narrow the time period or event type.',
          'Open an event to inspect details.',
          'Move to a domain-specific log when you need a focused view.'
        ],
        careful: [
          'Logs are audit events, not a full raw network traffic feed.',
          'Do not edit or hide logs to solve a support issue.'
        ]
      },
      {
        title: 'Employer logs',
        path: '/logs/employers',
        icon: Building2,
        simple: 'This page focuses on employer-related events.',
        useFor: [
          'Review employer account, KYC, job, hiring, billing, and communication activity.',
          'Confirm who changed employer status or verification state.'
        ],
        steps: [
          'Open Logs, then Employer logs.',
          'Search by employer, admin, action, or date.',
          'Open the event and read the before and after details where available.',
          'Use the event timeline to support your decision.'
        ],
        careful: [
          'Use employer logs when a company disputes a status, document, or action.'
        ]
      },
      {
        title: 'Candidate logs',
        path: '/logs/candidates',
        icon: UsersRound,
        simple: 'This page focuses on candidate-related events.',
        useFor: [
          'Review candidate profile, CV, matching, interview, assessment, offer, and notification activity.',
          'Investigate profile or account changes.'
        ],
        steps: [
          'Open Logs, then Candidate logs.',
          'Search by candidate, admin, action, or date.',
          'Open events linked to the issue.',
          'Compare the timeline with the candidate support request.'
        ],
        careful: [
          'Candidate data is personal. Only inspect logs for a valid support or compliance reason.'
        ]
      },
      {
        title: 'Job logs',
        path: '/logs/jobs',
        icon: Briefcase,
        simple: 'This page focuses on job-related events.',
        useFor: [
          'Review job creation, updates, status changes, matching actions, and job-related admin actions.',
          'Investigate why a job changed or why matching behavior changed.'
        ],
        steps: [
          'Open Logs, then Job logs.',
          'Search by job, employer, action, or date.',
          'Open the event details.',
          'Compare the event with the job page and job insights page.'
        ],
        careful: [
          'Job changes can affect matching results. Use logs before assuming the system made a mistake.'
        ]
      },
      {
        title: 'Interview logs',
        path: '/logs/interviews',
        icon: CalendarClock,
        simple: 'This page focuses on interview events.',
        useFor: [
          'Review scheduling, status changes, cancellations, and interview-related actions.',
          'Resolve interview timing or status disputes.'
        ],
        steps: [
          'Open Logs, then Interview logs.',
          'Search by candidate, employer, job, action, or date.',
          'Open the event and check the actor and timestamp.',
          'Use the details to explain what happened.'
        ],
        careful: [
          'Always check time and timezone before explaining an interview issue.'
        ]
      },
      {
        title: 'Assessment logs',
        path: '/logs/assessments',
        icon: ListChecks,
        simple: 'This page focuses on assessment events.',
        useFor: [
          'Review assessment creation, attempts, completion, scoring, and status changes.',
          'Investigate missing or disputed assessment results.'
        ],
        steps: [
          'Open Logs, then Assessment logs.',
          'Search by assessment, candidate, employer, job, or date.',
          'Open the relevant events.',
          'Compare the timeline with the assessment page.'
        ],
        careful: [
          'Assessment records can affect hiring. Do not change conclusions without evidence.'
        ]
      },
      {
        title: 'Offer logs',
        path: '/logs/offers',
        icon: FileSignature,
        simple: 'This page focuses on offer and contract events.',
        useFor: [
          'Review offer creation, negotiation, acceptance, decline, signing, upload, and withdrawal actions.',
          'Investigate offer document or signing disputes.'
        ],
        steps: [
          'Open Logs, then Offer logs.',
          'Search by offer, candidate, employer, job, action, or date.',
          'Open the event and check who did it and when.',
          'Use the offer page and logs together before responding to a dispute.'
        ],
        careful: [
          'Offer records may include sensitive contract information. Keep access limited.'
        ]
      }
    ]
  },
  {
    key: 'system',
    name: 'System',
    icon: Activity,
    summary: 'Use this section to watch platform health and operational risk.',
    pages: [
      {
        title: 'Monitoring',
        path: '/monitoring',
        icon: Activity,
        simple: 'This page shows whether the platform is healthy.',
        useFor: [
          'Check API, database, queue, matching, AI, runtime, and activity health where available.',
          'See critical warnings such as high memory use, slow work, or queue problems.',
          'Watch platform activity and inactivity risk.'
        ],
        steps: [
          'Open System, then Monitoring.',
          'Check the health summary first.',
          'Open any critical or warning item.',
          'Review the detailed cards for queues, matching, AI, database, and runtime signals.',
          'Escalate system issues if the problem affects users or keeps repeating.'
        ],
        careful: [
          'A critical warning should be investigated, not ignored.',
          'Some numbers are technical. Use the plain status and details to decide urgency.'
        ]
      }
    ]
  },
  {
    key: 'bottom-menu',
    name: 'Bottom menu',
    icon: ShieldCheck,
    summary: 'These controls are always at the bottom of the sidebar.',
    pages: [
      {
        title: 'My profile',
        path: '/profile',
        icon: KeyRound,
        simple: 'This page is for your own admin account.',
        useFor: [
          'View your admin profile, role, and permissions.',
          'Change your password.',
          'Understand what access your admin account has.'
        ],
        steps: [
          'Go to the bottom of the sidebar.',
          'Click My profile.',
          'Review your account details and permissions.',
          'To change your password, enter your current password, then your new password.',
          'After changing your password, sign in again when the system asks.'
        ],
        careful: [
          'Use a strong password and do not share it with anyone.',
          'If your role is wrong, ask an admin with RBAC permission to review it.'
        ]
      },
      {
        title: 'User guide',
        path: '/user-guide',
        icon: BookOpenCheck,
        simple: 'This is the page you are reading now.',
        useFor: [
          'Learn what each sidebar item does.',
          'Find step-by-step guidance for admin pages.',
          'Explain the Admin portal to a new team member.'
        ],
        steps: [
          'Go to the bottom of the sidebar.',
          'Click User guide.',
          'Use search to find a page, feature, or action.',
          'Read What you can do, Step by step, and Be careful before using a sensitive page.'
        ],
        careful: [
          'The guide explains the product. It does not replace company approval rules or security policy.'
        ]
      },
      {
        title: 'Log out',
        path: '/login',
        actionOnly: true,
        icon: ShieldCheck,
        simple: 'This ends your admin session on this browser.',
        useFor: [
          'Leave the Admin portal safely when you are done.',
          'Protect admin access on shared or public computers.'
        ],
        steps: [
          'Go to the bottom of the sidebar.',
          'Click Log out.',
          'Wait until the login page appears.',
          'Close the browser tab if you are using a shared machine.'
        ],
        careful: [
          'Always log out on a shared device.',
          'Do not rely only on closing the browser if someone else can use the same computer.'
        ]
      }
    ]
  }
];

const GUIDE_PRINCIPLES = [
  {
    title: 'Check first',
    text: 'Read the record, status, and history before changing anything.'
  },
  {
    title: 'Use clear notes',
    text: 'When you approve, reject, suspend, or review something, write notes another admin can understand.'
  },
  {
    title: 'Protect user data',
    text: 'Only open personal, company, payment, or document data when you have a valid reason.'
  },
  {
    title: 'Confirm impact',
    text: 'Some changes affect matching, payments, messages, or access. Know the impact before saving.'
  }
];

const normalize = (value) => String(value || '').toLowerCase();

const pageSearchText = (section, page) => [
  section.name,
  section.summary,
  page.title,
  page.path,
  page.simple,
  ...(page.useFor || []),
  ...(page.steps || []),
  ...(page.careful || [])
].join(' ');

function BulletList({ items, icon: Icon = CheckCircle2, tone = 'default' }) {
  if (!items?.length) return null;

  const toneClass = tone === 'warning' ? 'text-amber-600 bg-amber-50' : 'text-blue-600 bg-blue-50';

  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex gap-3 text-sm leading-6 text-slate-600">
          <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${toneClass}`}>
            <Icon size={14} />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StepList({ items }) {
  if (!items?.length) return null;

  return (
    <ol className="space-y-3">
      {items.map((item, index) => (
        <li key={item} className="flex gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-white">
            {index + 1}
          </span>
          <span className="pt-0.5 text-sm leading-6 text-slate-600">{item}</span>
        </li>
      ))}
    </ol>
  );
}

function GuidePageCard({ page }) {
  const Icon = page.icon || BookOpenCheck;

  return (
    <article id={`guide-${page.path.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`} className="scroll-mt-24 rounded-[24px] border border-slate-100 bg-white shadow-sm">
      <div className="border-b border-slate-100 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white">
              <Icon size={20} />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{page.path}</p>
              <h3 className="mt-1 text-xl font-semibold tracking-tight text-slate-950">{page.title}</h3>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{page.simple}</p>
            </div>
          </div>
          {page.actionOnly ? (
            <span className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-500">
              Sidebar action
            </span>
          ) : (
            <Link
              to={page.path}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Open page
              <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </div>

      <div className="grid gap-5 p-5 lg:grid-cols-[1fr_1fr]">
        <section className="rounded-2xl bg-slate-50 p-4">
          <h4 className="text-sm font-semibold text-slate-950">What you can do here</h4>
          <div className="mt-4">
            <BulletList items={page.useFor} />
          </div>
        </section>
        <section className="rounded-2xl bg-slate-50 p-4">
          <h4 className="text-sm font-semibold text-slate-950">Step by step</h4>
          <div className="mt-4">
            <StepList items={page.steps} />
          </div>
        </section>
        <section className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4 lg:col-span-2">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-amber-950">
            <AlertTriangle size={16} />
            Be careful
          </h4>
          <div className="mt-4">
            <BulletList items={page.careful} icon={AlertTriangle} tone="warning" />
          </div>
        </section>
      </div>
    </article>
  );
}

function AdminUserGuidePage() {
  const [search, setSearch] = useState('');
  const query = normalize(search).trim();

  const filteredSections = useMemo(() => GUIDE_SECTIONS.map((section) => ({
    ...section,
    pages: section.pages.filter((page) => !query || normalize(pageSearchText(section, page)).includes(query))
  })).filter((section) => section.pages.length), [query]);

  const totalPages = GUIDE_SECTIONS.reduce((total, section) => total + section.pages.length, 0);
  const visiblePages = filteredSections.reduce((total, section) => total + section.pages.length, 0);

  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 pt-20 font-sans sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1380px] space-y-6">
        <PageHeader
          eyebrow="Admin help"
          title="Admin User Guide"
          description="A plain-language guide to every Admin page in the sidebar. It explains what each page does, when to use it, and the safe steps to follow."
          actions={(
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm">
              <BookOpenCheck size={18} className="text-blue-600" />
              {totalPages} guided pages
            </div>
          )}
        />

        <section className="grid gap-4 md:grid-cols-4">
          {GUIDE_PRINCIPLES.map((item) => (
            <div key={item.title} className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <ShieldCheck size={18} />
              </span>
              <h2 className="mt-4 text-base font-semibold tracking-tight text-slate-950">{item.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{item.text}</p>
            </div>
          ))}
        </section>

        <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="xl:sticky xl:top-8 xl:h-fit">
            <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
              <label className="text-sm font-semibold text-slate-950" htmlFor="admin-guide-search">
                Find a page or action
              </label>
              <div className="relative mt-3">
                <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="admin-guide-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search KYC, billing, logs..."
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-300 focus:ring-4 focus:ring-blue-50"
                />
              </div>

              <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                Showing <span className="font-semibold text-slate-950">{visiblePages}</span> of{' '}
                <span className="font-semibold text-slate-950">{totalPages}</span> guide pages.
              </div>

              <nav className="mt-5 max-h-[62vh] space-y-2 overflow-y-auto pr-1">
                {filteredSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <a
                      key={section.key}
                      href={`#section-${section.key}`}
                      className="group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition group-hover:bg-slate-950 group-hover:text-white">
                        <Icon size={17} />
                      </span>
                      <span className="min-w-0 flex-1 truncate">{section.name}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                        {section.pages.length}
                      </span>
                    </a>
                  );
                })}
              </nav>
            </div>
          </aside>

          <main className="space-y-7">
            {filteredSections.length ? (
              filteredSections.map((section) => {
                const Icon = section.icon;
                return (
                  <section key={section.key} id={`section-${section.key}`} className="scroll-mt-24 space-y-4">
                    <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
                      <div className="flex items-start gap-4">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                          <Icon size={20} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Sidebar section</p>
                          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">{section.name}</h2>
                          <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">{section.summary}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {section.pages.map((page) => (
                        <GuidePageCard key={page.path + page.title} page={page} />
                      ))}
                    </div>
                  </section>
                );
              })
            ) : (
              <div className="rounded-[24px] border border-slate-100 bg-white p-8 text-center shadow-sm">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                  <Search size={20} />
                </span>
                <h2 className="mt-4 text-lg font-semibold tracking-tight text-slate-950">No guide section found</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">
                  Try a different word, such as KYC, billing, logs, WhatsApp, candidates, offers, or password.
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default AdminUserGuidePage;
